// CON-1621 — Message Template context scope.
//
// A template's usable context is derived server-side from its Smart Field
// token prefixes ([SFE:] -> event, [SFG:] -> group, neither -> contact) on
// every save, and returned on the template view model as contextScope.
// Group/Event colors mirror the entity chip convention in
// SelectFieldToInsert (ENTITY_CHIP_CONFIG); contact is deliberately muted
// grey rather than the Contact-entity purple, because contact scope means
// "works anywhere", not "contacts only".

// CON-1621 follow-up — the LEGACY dynamicFields tokens that carry scope.
// Mirrors LEGACY_TOKEN_SCOPE in the server's constants/followUpMessages.js
// and MUST be kept in step with it: the server decides the stored scope on
// save, this copy only drives the live badge while the user types. If they
// drift, the badge lies until the row is saved.
//
// Legacy tokens carry no prefix, so scope cannot be parsed from the text
// the way [SFE:]/[SFG:] can: [Host First Name] and [First Name] are
// indistinguishable to a parser. Hence the explicit list.
export const LEGACY_TOKEN_SCOPE = {
  '[All Event Guests Names]': 'event',
  '[All Party Guests Names]': 'event',
  '[Event Address 1]': 'event',
  '[Event Address 2]': 'event',
  '[Event City]': 'event',
  '[Event End Date]': 'event',
  '[Event End Time]': 'event',
  '[Event Host URL]': 'event',
  '[Event Name]': 'event',
  '[Event Postal]': 'event',
  '[Event Shopping URL]': 'event',
  '[Event Start Date]': 'event',
  '[Event Start Time]': 'event',
  '[Event State]': 'event',
  '[Event Subtotal]': 'event',
  '[Guests Who Booked Maybe]': 'event',
  '[Guests Who Booked Said NO]': 'event',
  '[Guests Who Booked Yes]': 'event',
  '[Guests Who Did Not Order]': 'event',
  '[Guests Who Ordered]': 'event',
  '[Host First Name]': 'event',
  '[Host Last Name]': 'event',
  '[Join Form URL]': 'event',
  '[Number of Guests]': 'event',
  '[Party Address 1]': 'event',
  '[Party Address 2]': 'event',
  '[Party City]': 'event',
  '[Party End Date]': 'event',
  '[Party End Time]': 'event',
  '[Party Guests Who Booked Maybe]': 'event',
  '[Party Guests Who Booked Said NO]': 'event',
  '[Party Guests Who Booked Yes]': 'event',
  '[Party Guests Who Did Not Order]': 'event',
  '[Party Guests Who Ordered]': 'event',
  '[Party Host URL]': 'event',
  '[Party Join Form URL]': 'event',
  '[Party Name]': 'event',
  '[Party Postal]': 'event',
  '[Party Shopping URL]': 'event',
  '[Party Start Date]': 'event',
  '[Party Start Time]': 'event',
  '[Party State]': 'event',
  '[Party Subtotal]': 'event',
  '[Group Name]': 'group',
  '[Group Type]': 'group',
  '[Group Description]': 'group',
};

// Returns { event?: token, group?: token } for the scope-bearing legacy
// tokens present in a body. First match per scope, which is what the
// mixed-scope warning names.
export function findLegacyTokenScopes(text) {
  const found = {};
  if (!text) return found;
  Object.keys(LEGACY_TOKEN_SCOPE).forEach((token) => {
    const scope = LEGACY_TOKEN_SCOPE[token];
    if (!found[scope] && text.includes(token)) found[scope] = token;
  });
  return found;
}

export const TEMPLATE_CONTEXT_SCOPE = {
  CONTACT: 'contact',
  GROUP: 'group',
  EVENT: 'event',
};

export const TEMPLATE_CONTEXT_SCOPE_CONFIG = {
  [TEMPLATE_CONTEXT_SCOPE.CONTACT]: {
    label: 'Use anywhere',
    bg: '#F0F0F0',
    color: '#5A5A5A',
    dotColor: '#9E9E9E',
    explanation: 'Works anywhere you send a message.',
  },
  [TEMPLATE_CONTEXT_SCOPE.GROUP]: {
    label: 'Groups only',
    bg: '#E3F2FD',
    color: '#1565C0',
    dotColor: '#1565C0',
    explanation: 'Uses Group Smart Fields. They only fill in when texting in a group.',
  },
  [TEMPLATE_CONTEXT_SCOPE.EVENT]: {
    label: 'Events only',
    bg: '#FFF3E0',
    color: '#E65100',
    dotColor: '#E65100',
    explanation: 'Uses Event Smart Fields. They only fill in when texting event guests.',
  },
};

// CON-1621 P1-C — which template scopes a picker on a given surface may
// offer, sent to POST /template-tag as `contexts`.
//
// Contact scope resolves on every send path, so it is always included. A
// surface is widened to Group or Event ONLY where that surface's send path
// was verified to resolve those tokens; anything unmapped falls through to
// contact-only, matching the endpoint's own fail-closed default. Offering a
// template whose Smart Fields cannot resolve there would send a blank where
// the token was, so silently showing fewer templates is the safe failure.
const TEMPLATE_CONTEXTS_BY_ENTITY = {
  Event: [TEMPLATE_CONTEXT_SCOPE.CONTACT, TEMPLATE_CONTEXT_SCOPE.EVENT],
  PartyGuest: [TEMPLATE_CONTEXT_SCOPE.CONTACT, TEMPLATE_CONTEXT_SCOPE.EVENT],
  Group: [TEMPLATE_CONTEXT_SCOPE.CONTACT, TEMPLATE_CONTEXT_SCOPE.GROUP],
  GroupMember: [TEMPLATE_CONTEXT_SCOPE.CONTACT, TEMPLATE_CONTEXT_SCOPE.GROUP],
};

// SCREEN_NAMES values -> the same entity keys, so the mapping lives once.
const ENTITY_BY_SCREEN_NAME = {
  'Event List': 'Event',
  'Event Guest List': 'Event',
  'Group List': 'Group',
  'Group Contact List': 'Group',
};

export const getTemplateContextsForEntity = (entityContext) => (
  TEMPLATE_CONTEXTS_BY_ENTITY[entityContext] || [TEMPLATE_CONTEXT_SCOPE.CONTACT]
);

export const getTemplateContextsForScreen = (screenName) => (
  getTemplateContextsForEntity(ENTITY_BY_SCREEN_NAME[screenName])
);

// CON-1621 P1-C — belt and braces for the shared Redux slices.
//
// Several `followUpMessages` slices move in lockstep because four action
// files declare the same `@orders/get-follow-up-messages` type strings, and
// others (messagingSettings) are shared outright by unrelated screens. Each
// screen refetches on mount, so it is last-fetch-wins and self-correcting,
// but between a widened surface's fetch and a contact-only surface's own
// fetch resolving, a slice can briefly hold Group/Event templates. Filtering
// the option list at render closes that window: the server default is the
// safety net, this is the per-surface enforcement.
export const filterTemplatesByContexts = (templates, contexts) => {
  const allowed = Array.isArray(contexts) && contexts.length
    ? contexts
    : [TEMPLATE_CONTEXT_SCOPE.CONTACT];
  return (Array.isArray(templates) ? templates : []).filter((template) => (
    allowed.includes(template?.contextScope || TEMPLATE_CONTEXT_SCOPE.CONTACT)
  ));
};

export const getTemplateContextScopeConfig = (contextScope) => (
  TEMPLATE_CONTEXT_SCOPE_CONFIG[contextScope]
  || TEMPLATE_CONTEXT_SCOPE_CONFIG[TEMPLATE_CONTEXT_SCOPE.CONTACT]
);

// Options for the list screen's scope selector (SelectStatus shape).
// Default is ALL — a management screen must show everything.
export const TEMPLATE_CONTEXT_SCOPE_OPTIONS = [
  { title: 'All Scopes', value: '', active: true },
  {
    title: 'Use anywhere',
    value: TEMPLATE_CONTEXT_SCOPE.CONTACT,
    dotColor: TEMPLATE_CONTEXT_SCOPE_CONFIG.contact.dotColor,
  },
  {
    title: 'Groups only',
    value: TEMPLATE_CONTEXT_SCOPE.GROUP,
    dotColor: TEMPLATE_CONTEXT_SCOPE_CONFIG.group.dotColor,
  },
  {
    title: 'Events only',
    value: TEMPLATE_CONTEXT_SCOPE.EVENT,
    dotColor: TEMPLATE_CONTEXT_SCOPE_CONFIG.event.dotColor,
  },
];
